import {
  hsvToRgb,
  mergeSwatches,
  opaqueHex,
  paintColors,
  parseHex,
  resolvePrintProfile,
  rgbToHsv,
  type SceneDocument,
  toHex,
} from "@cva/shared";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Link2, Pipette, Printer } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { client, orpc } from "../lib/api";
import { cn } from "../lib/utils";
import { NoBrandKitHint } from "./BrandHint";
import { ColorSuggestions } from "./ColorSuggestions";
import { useEnv } from "./env";
import { pickScreenColor } from "./eyedropper";
import { useEditor, useEditorApi } from "./store";
import { Popover, PopoverContent, PopoverTrigger } from "./ui";

export const DEFAULT_COLORS = [
  "#000000",
  "#545454",
  "#a6a6a6",
  "#d9d9d9",
  "#ffffff",
  "#e11d48",
  "#f97316",
  "#facc15",
  "#22c55e",
  "#14b8a6",
  "#0ea5e9",
  "#2563eb",
  "#4f46e5",
  "#9333ea",
  "#db2777",
];

/** All colours used in a document (most used first). */
export function documentColors(doc: SceneDocument): string[] {
  const counts = new Map<string, number>();
  const add = (color: string | undefined) => {
    if (!color) return;
    const key = color.toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  };
  for (const page of doc.pages) paintColors(page.background).forEach(add);
  for (const element of Object.values(doc.elements)) {
    if (element.type === "text") {
      add(element.style.color);
      add(element.style.stroke?.color);
    }
    if (element.type === "shape") {
      paintColors(element.fill).forEach(add);
      add(element.stroke?.color);
    }
    if (element.type === "image") add(element.stroke?.color);
    if (element.type === "icon") add(element.color);
    if (element.type === "chart") {
      add(element.color);
      for (const series of element.series) add(series.color);
      for (const color of element.palette ?? []) add(color);
    }
  }
  for (const swatch of doc.format.print?.swatches ?? []) add(swatch.color);
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([color]) => color)
    .slice(0, 20);
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** CMYK values and gamut warning for the print profile of the document. */
export function useColorReport(color: string, enabled: boolean) {
  const env = useEnv();
  const print = useEditor((s) => s.doc.format.print);
  const brand = useEditor((s) => s.brand);
  const debounced = useDebounced(opaqueHex(color), 250);
  const profile = print ? resolvePrintProfile(print).id : "pso-coated-v3";
  // Exact CMYK values of the brand kit count like the document's own swatches.
  const swatches = brand ? mergeSwatches(print?.swatches, brand) : (print?.swatches ?? []);
  return useQuery({
    ...orpc.print.colors.queryOptions({
      input: { orgId: env.orgId, profile, colors: [debounced], swatches },
    }),
    enabled: enabled && Boolean(print),
    staleTime: Number.POSITIVE_INFINITY,
    select: (reports) => reports[0],
  });
}

function SaturationArea({
  hsv,
  onChange,
}: {
  hsv: { h: number; s: number; v: number };
  onChange: (s: number, v: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const update = (event: React.PointerEvent) => {
    const rect = ref.current!.getBoundingClientRect();
    const s = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const v = Math.max(0, Math.min(1, 1 - (event.clientY - rect.top) / rect.height));
    onChange(s, v);
  };
  const hue = toHex({ ...hsvToRgb({ h: hsv.h, s: 1, v: 1 }), a: 1 });
  return (
    <div
      ref={ref}
      className="relative h-36 w-full cursor-crosshair touch-none rounded-lg"
      style={{
        background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hue})`,
      }}
      onPointerDown={(event) => {
        (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
        update(event);
      }}
      onPointerMove={(event) => {
        if (event.buttons) update(event);
      }}
    >
      <div
        className="pointer-events-none absolute size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow ring-1 ring-black/20"
        style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
      />
    </div>
  );
}

function ChannelSlider({
  value,
  max,
  background,
  onChange,
}: {
  value: number;
  max: number;
  background: string;
  onChange: (v: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const update = (event: React.PointerEvent) => {
    const rect = ref.current!.getBoundingClientRect();
    onChange(Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)) * max);
  };
  return (
    <div
      ref={ref}
      className="relative h-3 w-full cursor-pointer touch-none rounded-full"
      style={{ background }}
      onPointerDown={(event) => {
        (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
        update(event);
      }}
      onPointerMove={(event) => {
        if (event.buttons) update(event);
      }}
    >
      <div
        className="pointer-events-none absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow ring-1 ring-black/20"
        style={{ left: `${(value / max) * 100}%` }}
      />
    </div>
  );
}

const CHECKER = "repeating-conic-gradient(#e4e4e7 0% 25%, #fff 0% 50%) 50% / 10px 10px";

export function Swatch({
  color,
  className,
  onClick,
  title,
}: {
  color: string;
  className?: string;
  onClick?: () => void;
  title?: string;
}) {
  return (
    <button
      type="button"
      title={title ?? color}
      onClick={onClick}
      className={cn(
        "size-6 shrink-0 overflow-hidden rounded-md ring-1 ring-black/10 ring-inset",
        className,
      )}
      style={{ background: CHECKER }}
    >
      <span className="block size-full" style={{ background: color }} />
    </button>
  );
}

/** Brand link of a colour field: the linked token and how to link another one. */
export interface BrandLink {
  token: string | undefined;
  onToken: (key: string) => void;
}

/** Name of the linked brand colour, if the link is valid. */
export function useBrandColorName(token: string | undefined): string | undefined {
  return useEditor((s) =>
    token ? s.brand?.colors.find((color) => color.key === token)?.name : undefined,
  );
}

/** Colour editor (inside a popover). */
export function ColorEditor({
  value,
  onChange,
  allowAlpha = true,
  brandLink,
}: {
  value: string;
  onChange: (color: string) => void;
  allowAlpha?: boolean;
  /** Offer brand colours as linked tokens (otherwise they are plain colours). */
  brandLink?: BrandLink;
}) {
  const store = useEditorApi();
  const env = useEnv();
  const doc = useEditor((s) => s.doc);
  const brand = useEditor((s) => s.brand);
  const print = doc.format.print;
  const rgba = parseHex(value);
  const [hsv, setHsv] = useState(() => rgbToHsv(rgba));
  const [hex, setHex] = useState(value);
  const [cmykInput, setCmykInput] = useState<[number, number, number, number] | null>(null);
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value.toLowerCase() !== lastEmitted.current.toLowerCase()) {
      setHsv(rgbToHsv(parseHex(value)));
      lastEmitted.current = value;
    }
    setHex(value);
  }, [value]);

  const emit = (next: string) => {
    lastEmitted.current = next;
    setHex(next);
    onChange(next);
  };
  const sampleColor = async () => {
    if (store.getState().readOnly) return;
    const color = await pickScreenColor();
    if (!color || store.getState().readOnly) return;
    const next = toHex(parseHex(color));
    setHsv(rgbToHsv(parseHex(next)));
    emit(next);
  };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target;
      if (
        event.defaultPrevented ||
        event.repeat ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        event.shiftKey
      )
        return;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
      )
        return;
      if (event.key.toLowerCase() !== "i") return;
      event.preventDefault();
      void sampleColor();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  });
  const fromHsv = (next: { h: number; s: number; v: number }, alpha = rgba.a) => {
    setHsv(next);
    emit(toHex({ ...hsvToRgb(next), a: alpha }));
  };
  const report = useColorReport(value, Boolean(print));
  const swatch = print?.swatches?.find((entry) => opaqueHex(entry.color) === opaqueHex(value));
  const colors = documentColors(doc);

  const applyCmyk = async (cmyk: [number, number, number, number]) => {
    if (!print) return;
    const profile = resolvePrintProfile(print).id;
    const { color } = await client.print.proof({ orgId: env.orgId, profile, cmyk });
    // Remember the exact CMYK value for this colour and use its screen preview.
    store.getState().update((draft) => {
      const target = draft.format.print;
      if (!target) return;
      const swatches = (target.swatches ?? []).filter((entry) => opaqueHex(entry.color) !== color);
      swatches.push({ color, cmyk });
      target.swatches = swatches;
    });
    setCmykInput(null);
    setHsv(rgbToHsv(parseHex(color)));
    emit(color);
  };

  return (
    <div className="space-y-3">
      {brand && brand.colors.length > 0 ? (
        <div>
          <p className="mb-1.5 flex items-center gap-1 text-xs font-medium text-zinc-500">
            Marke
            {brandLink?.token ? (
              <span className="inline-flex items-center gap-0.5 text-brand-700">
                · <Link2 className="size-3" /> verknüpft
              </span>
            ) : null}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {brand.colors.map((color) => (
              <Swatch
                key={color.key}
                color={color.color}
                title={`${color.name} (${color.color.toUpperCase()})${brandLink ? " – mit der Marke verknüpfen" : ""}`}
                className={cn(
                  brandLink?.token === color.key && "ring-2 ring-brand-600 ring-offset-1",
                )}
                onClick={() => {
                  setHsv(rgbToHsv(parseHex(color.color)));
                  if (brandLink) {
                    lastEmitted.current = color.color;
                    setHex(color.color);
                    brandLink.onToken(color.key);
                  } else emit(color.color);
                }}
              />
            ))}
          </div>
        </div>
      ) : env.brandMissing ? (
        <NoBrandKitHint />
      ) : null}
      <SaturationArea hsv={hsv} onChange={(s, v) => fromHsv({ ...hsv, s, v })} />
      <ChannelSlider
        value={hsv.h}
        max={360}
        background="linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)"
        onChange={(h) => fromHsv({ ...hsv, h: Math.min(359.9, h) })}
      />
      {allowAlpha ? (
        <ChannelSlider
          value={rgba.a}
          max={1}
          background={`linear-gradient(to right, transparent, ${opaqueHex(value)}), ${CHECKER}`}
          onChange={(a) => fromHsv(hsv, Math.round(a * 100) / 100)}
        />
      ) : null}
      <div className="flex items-center gap-2">
        <Swatch color={value} className="size-8" />
        <input
          value={hex}
          onChange={(event) => setHex(event.target.value)}
          onBlur={() => {
            const normalized = hex.startsWith("#") ? hex : `#${hex}`;
            if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(normalized)) {
              const next = toHex(parseHex(normalized));
              setHsv(rgbToHsv(parseHex(next)));
              emit(next);
            } else setHex(value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") (event.currentTarget as HTMLInputElement).blur();
          }}
          className="h-8 w-full rounded-md border border-zinc-200 px-2 font-mono text-sm uppercase outline-none focus:border-brand-500"
        />
        {"EyeDropper" in window ? (
          <button
            type="button"
            title="Farbe vom Bildschirm aufnehmen (I)"
            aria-label="Farbe vom Bildschirm aufnehmen (I)"
            className="inline-flex size-8 shrink-0 items-center justify-center rounded-md border border-zinc-200 text-zinc-600 hover:bg-zinc-50"
            onClick={() => void sampleColor()}
          >
            <Pipette className="size-4" />
          </button>
        ) : null}
      </div>

      {print ? (
        <div className="rounded-lg border border-zinc-200 p-2.5 text-xs">
          <div className="mb-1.5 flex items-center gap-1.5 font-medium text-zinc-700">
            <Printer className="size-3.5" /> Druck (CMYK)
          </div>
          {report.data ? (
            <>
              <div className="flex items-center gap-2">
                <span className="font-mono text-zinc-800 tabular-nums">
                  {report.data.cmyk.map((v) => Math.round(v)).join(" / ")}
                </span>
                <span className="text-zinc-400">
                  {report.data.source === "swatch"
                    ? "exakt festgelegt"
                    : report.data.source === "black"
                      ? "reines Schwarz"
                      : report.data.source === "gray"
                        ? "nur Schwarzplatte"
                        : "aus Profil"}
                </span>
              </div>
              {report.data.outOfGamut ? (
                <div className="mt-2 flex items-start gap-2 rounded-md bg-amber-50 p-2 text-amber-900">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p>
                      Diese Farbe lässt sich nicht exakt drucken. So ungefähr sieht sie im Druck
                      aus:
                    </p>
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <Swatch color={opaqueHex(value)} title="Bildschirm" />
                      <span>→</span>
                      <Swatch color={report.data.proof} title="Druck" />
                      <button
                        type="button"
                        className="ml-auto font-medium underline"
                        onClick={() => {
                          setHsv(rgbToHsv(parseHex(report.data!.proof)));
                          emit(report.data!.proof);
                        }}
                      >
                        Übernehmen
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <p className="text-zinc-400">Wird berechnet …</p>
          )}
          {cmykInput ? (
            <div className="mt-2 space-y-2">
              <div className="grid grid-cols-4 gap-1">
                {(["C", "M", "Y", "K"] as const).map((channel, index) => (
                  <label
                    key={channel}
                    className="flex items-center rounded border border-zinc-200 px-1"
                  >
                    <span className="text-zinc-400">{channel}</span>
                    <input
                      className="w-full bg-transparent text-right tabular-nums outline-none"
                      value={cmykInput[index]}
                      inputMode="numeric"
                      onChange={(event) => {
                        const next = [...cmykInput] as [number, number, number, number];
                        next[index] = Math.max(
                          0,
                          Math.min(100, Number(event.target.value.replace(/\D/g, "")) || 0),
                        );
                        setCmykInput(next);
                      }}
                    />
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" className="text-zinc-500" onClick={() => setCmykInput(null)}>
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="font-medium text-brand-700"
                  onClick={() => void applyCmyk(cmykInput)}
                >
                  Übernehmen
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-1.5 flex gap-3">
              <button
                type="button"
                className="font-medium text-brand-700 hover:underline"
                onClick={() =>
                  setCmykInput(
                    (swatch?.cmyk ?? report.data?.cmyk ?? [0, 0, 0, 100]).map((v) =>
                      Math.round(v),
                    ) as [number, number, number, number],
                  )
                }
              >
                Exakte CMYK-Werte festlegen
              </button>
              {swatch ? (
                <button
                  type="button"
                  className="text-zinc-500 hover:underline"
                  onClick={() =>
                    store.getState().update((draft) => {
                      const target = draft.format.print;
                      if (target?.swatches) {
                        target.swatches = target.swatches.filter(
                          (entry) => opaqueHex(entry.color) !== opaqueHex(value),
                        );
                      }
                    })
                  }
                >
                  Lösen
                </button>
              ) : null}
            </div>
          )}
        </div>
      ) : null}

      {colors.length > 0 ? (
        <div>
          <p className="mb-1.5 text-xs font-medium text-zinc-500">In dieser Datei</p>
          <div className="flex flex-wrap gap-1.5">
            {colors.map((color) => (
              <Swatch
                key={color}
                color={color}
                onClick={() => {
                  setHsv(rgbToHsv(parseHex(color)));
                  emit(color);
                }}
              />
            ))}
          </div>
        </div>
      ) : null}
      <ColorSuggestions
        value={value}
        onPick={(color) => {
          setHsv(rgbToHsv(parseHex(color)));
          emit(color);
        }}
      />
      <div>
        <p className="mb-1.5 text-xs font-medium text-zinc-500">Standardfarben</p>
        <div className="flex flex-wrap gap-1.5">
          {DEFAULT_COLORS.map((color) => (
            <Swatch
              key={color}
              color={color}
              onClick={() => {
                setHsv(rgbToHsv(parseHex(color)));
                emit(color);
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Swatch button that opens the colour editor. Changes during one open popover are one undo step. */
export function ColorButton({
  value,
  onChange,
  allowAlpha,
  label,
  children,
  brandLink,
}: {
  value: string;
  onChange: (color: string) => void;
  allowAlpha?: boolean;
  label?: string;
  children?: ReactNode;
  brandLink?: BrandLink;
}) {
  const store = useEditorApi();
  const print = useEditor((s) => Boolean(s.doc.format.print));
  const report = useColorReport(value, print);
  const brandName = useBrandColorName(brandLink?.token);
  return (
    <Popover
      onOpenChange={(open) => {
        if (open) store.getState().beginGesture({ live: true });
        else store.getState().endGesture();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          title={label ?? "Farbe"}
          className="relative inline-flex h-8 items-center gap-2 rounded-md border border-zinc-200 bg-white pr-2 pl-1 text-xs hover:bg-zinc-50"
        >
          <span
            className="size-6 overflow-hidden rounded ring-1 ring-black/10 ring-inset"
            style={{ background: CHECKER }}
          >
            <span className="block size-full" style={{ background: value }} />
          </span>
          {brandName ? (
            <span
              className="inline-flex items-center gap-1 text-brand-700"
              title="Mit der Marke verknüpft"
            >
              <Link2 className="size-3" />
              {brandName}
            </span>
          ) : (
            (children ?? (
              <span className="font-mono text-zinc-600 uppercase">{opaqueHex(value)}</span>
            ))
          )}
          {report.data?.outOfGamut ? (
            <AlertTriangle
              className="size-3.5 text-amber-500"
              aria-label="Im Druck nicht darstellbar"
            />
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent>
        <ColorEditor
          value={value}
          onChange={onChange}
          allowAlpha={allowAlpha}
          brandLink={brandLink}
        />
      </PopoverContent>
    </Popover>
  );
}
